import { createFileRoute, notFound, redirect, Link, Outlet } from "@tanstack/react-router";
import { pickupEstimateLabel, pickupTimeEstimate } from "@/lib/pickup-time";
import { queryOptions, useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Breadcrumbs, type Crumb } from "@/components/storefront/Breadcrumbs";
import { BrandLogo } from "@/components/storefront/BrandLogo";
import { PlayfulNotFound } from "@/components/storefront/PlayfulError";
import { UnavailableProduct } from "@/components/storefront/UnavailableProduct";
import { getProductByIdPrefix, getUnavailableProductInfo, getLegacyRedirectTarget, listCategories, getManufacturerByPsId, listProductsByIds, getProductImages } from "@/lib/products.functions";
import { listRelatedProducts } from "@/lib/products.functions";
import { reviewsQueryOptions } from "@/lib/reviews-query";
import { lazy, Suspense } from "react";

const ProductReviews = lazy(() => import("@/components/storefront/ProductReviews"));
import { RatingStars } from "@/components/storefront/RatingStars";
import { ProductCard } from "@/components/storefront/ProductCard";
import { CrossSell } from "@/components/storefront/CrossSell";
import { ChristmasStrip } from "@/components/storefront/ChristmasStrip";
import { formatPrice, idFromSlug, greeklish, resolveAvailability, availabilityColor, productImageAlt } from "@/lib/format";
import { validGtin } from "@/lib/gtin";
import { isRecalled } from "@/lib/recalled-products";
import { isRestricted } from "@/lib/restricted-products";
import { isDiscontinued } from "@/lib/discontinued-products";
import { DiscontinuedPopup } from "@/components/storefront/DiscontinuedPopup";
import { ProductImage } from "@/components/storefront/ProductImage";
import { useCart } from "@/hooks/storefront/use-cart";
import { useWishlist } from "@/hooks/storefront/use-wishlist";
import { useRecentlyViewed } from "@/hooks/storefront/use-recently-viewed";
import { useFragileProducts } from "@/hooks/storefront/use-fragile";
import { Heart, ShoppingCart, Search, X, Facebook, Twitter, MessageCircle, Send, Link as LinkIcon, Share2, Sparkles, Factory, Clock, Store, PackageX } from "lucide-react";
import { ImageZoomLens } from "@/components/storefront/ImageZoomLens";
import { useEffect, useMemo, useRef, useState } from "react";
import { CART_DRAG_MIME, flyToCart, hideCartDropHint, serializeDrag, setPlayfulProductDragImage, showCartDropHint } from "@/lib/fly-to-cart";
import { buildCategoryIconMap } from "@/lib/category-icon";
import { mark } from "@/lib/user-timing";
import { OfferCountdown } from "@/components/storefront/OfferCountdown";
import { StorePickupDialog } from "@/components/storefront/StorePickupDialog";
import { ExpiryBadge } from "@/components/storefront/ExpiryBadge";
import { HealthScoreBanner } from "@/components/storefront/HealthScore";
import { IngredientAnalysis, IngredientScoreBanner } from "@/components/storefront/IngredientAnalysis";
import { NutritionFacts } from "@/components/storefront/NutritionFacts";
import { sanitizeHtml } from "@/lib/sanitize-html";
import ogShare from "@/assets/og-share.png.asset.json";
import { retryAsync } from "@/lib/ssr-retry";
import { computeUnitPrice } from "@/lib/unit-price";
import { KlarnaMessaging } from "@/components/storefront/KlarnaMessaging";

// Fallback product image so Merchant listing structured data always has the
// required `image` field, even for products missing an image_url.
const FALLBACK_IMAGE = `https://mrpanda.gr${ogShare.url}`;

// Product HTML is untrusted catalog data: sanitize with the shared allow-list
// sanitizer before rendering with dangerouslySetInnerHTML.

function hasHtmlTags(s: string | null | undefined): boolean {
  return !!s && /<[a-z][\s\S]*>/i.test(s);
}

function hasVisibleContent(html: string): boolean {
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
}

// Tailwind descendant utilities to render arbitrary product HTML attractively.
const proseClass =
  "max-w-none text-sm leading-relaxed text-muted-foreground " +
  "[&_p]:mb-3 [&_br]:content-[''] " +
  "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 " +
  "[&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_strong]:text-foreground [&_b]:font-semibold " +
  "[&_em]:italic [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-foreground " +
  "[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:font-semibold [&_h3]:text-foreground " +
  "[&_img]:my-3 [&_img]:max-w-full [&_img]:rounded-md [&_table]:my-3 [&_table]:w-full [&_td]:border [&_td]:border-border [&_td]:p-2";

const qo = (idPrefix: string) =>
  queryOptions({
    queryKey: ["product", idPrefix],
    queryFn: () => getProductByIdPrefix({ data: { idPrefix } }),
  });
const categoriesQO = queryOptions({
  queryKey: ["categories"],
  queryFn: () => listCategories(),
});

export const Route = createFileRoute("/p/$slug")({
  head: ({ params, loaderData }) => {
    const product = (loaderData as { product?: Record<string, unknown> } | undefined)?.product;
    // Μη διαθέσιμο (ανενεργό/διαγραμμένο) προϊόν: κρατάμε ζωντανό το URL με
    // κανονικό τίτλο/περιγραφή, αφού η σελίδα δείχνει πλέον χρήσιμο περιεχόμενο.
    const unavailable = (loaderData as { unavailable?: { status?: string; product?: { name?: string | null; image_url?: string | null } | null } } | undefined)?.unavailable;
    const reviewSummary = (loaderData as { reviewSummary?: { count: number; average: number } } | undefined)?.reviewSummary;
    const unavailableName = (unavailable?.product?.name ?? "").trim();
    const rawName = (product?.name as string | undefined)?.trim() || unavailableName || "";
    const hasValidName = rawName.length > 0;
    const name =
      rawName || params.slug.split("-").slice(0, -1).join(" ");

    const url = `https://mrpanda.gr/p/${params.slug}`;
    const stripHtml = (s: unknown) =>
      typeof s === "string" ? s.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim() : "";
    const rawDesc =
      stripHtml(product?.description_short) || stripHtml(product?.description);
    const description = unavailable
      ? (unavailable.status === "inactive"
          ? `${name}: προσωρινά μη διαθέσιμο στο MrPanda.gr. Δες σχετικά προϊόντα, προϊόντα της ίδιας μάρκας και της ίδιας κατηγορίας.`
          : `${name}: το προϊόν έχει καταργηθεί. Δες σχετικές εναλλακτικές προτάσεις στο MrPanda.gr.`).slice(0, 160)
      : (rawDesc || `Αγόρασε ${name} στο MrPanda.gr σε χαμηλή τιμή, με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα.`).slice(0, 160);
    const image = (product?.image_url as string | undefined) || (unavailable?.product?.image_url ?? undefined) || undefined;

    // Always have an image for structured data (Google Merchant listings require it).
    const ldImage = image || FALLBACK_IMAGE;
    const price = product?.price != null ? Number(product.price) : undefined;
    const stock = Number((product?.stock_quantity as number | undefined) ?? 0);
    const availability = stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";

    const scripts: { type: string; children: string }[] = [];
    const recalled = isRecalled(product?.ps_id as number | null | undefined);
    const restricted = isRestricted(product?.ps_id as number | null | undefined);
    const discontinued = isDiscontinued(product?.ps_id as number | null | undefined, product?.name as string | null | undefined);
    if (product && hasValidName && !recalled && !restricted && !discontinued) {
      const productLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name,
        url,
        image: ldImage,
        description:
          ((rawDesc && rawDesc.length >= 30 ? rawDesc : description) ||
            `${name} — διαθέσιμο στο MrPanda.gr σε χαμηλή τιμή, με ασφαλείς πληρωμές και γρήγορη παράδοση σε όλη την Ελλάδα.`).slice(0, 5000),
        ...(product.manufacturer_name ? { brand: { "@type": "Brand", name: product.manufacturer_name } } : {}),
        ...(product.reference ? { sku: String(product.reference), mpn: String(product.reference) } : {}),
        ...(() => {
          const g =
            validGtin((product as { ean13?: string | null }).ean13) ||
            validGtin((product as { upc?: string | null }).upc) ||
            validGtin(product.reference as string | null | undefined);
          return g ? { gtin: g, ...(g.length === 13 ? { gtin13: g } : {}) } : {};
        })(),
      };
      if (reviewSummary && reviewSummary.count > 0) {
        productLd.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: reviewSummary.average,
          reviewCount: reviewSummary.count,
          bestRating: 5,
          worstRating: 1,
        };
      }
      if (price != null && !Number.isNaN(price)) {
        productLd.offers = {
          "@type": "Offer",
          price: price.toFixed(2),
          priceCurrency: "EUR",
          availability,
          url,
          priceValidUntil: new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10),
          seller: { "@type": "Organization", name: "MrPanda.gr", url: "https://mrpanda.gr" },
          itemCondition: "https://schema.org/NewCondition",
          shippingDetails: {
            "@type": "OfferShippingDetails",
            shippingRate: {
              "@type": "MonetaryAmount",
              value: "3.00",
              currency: "EUR",
            },
            shippingDestination: {
              "@type": "DefinedRegion",
              addressCountry: "GR",
            },
            deliveryTime: {
              "@type": "ShippingDeliveryTime",
              handlingTime: {
                "@type": "QuantitativeValue",
                minValue: 0,
                maxValue: 1,
                unitCode: "DAY",
              },
              transitTime: {
                "@type": "QuantitativeValue",
                minValue: 1,
                maxValue: 3,
                unitCode: "DAY",
              },
            },
          },
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry: "GR",
            returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
            merchantReturnDays: 14,
            returnMethod: "https://schema.org/ReturnByMail",
            returnFees: "https://schema.org/ReturnShippingFees",
            returnShippingFeesAmount: {
              "@type": "MonetaryAmount",
              value: "3.00",
              currency: "EUR",
            },
          },
        };
      }
      scripts.push({ type: "application/ld+json", children: JSON.stringify(productLd) });
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://mrpanda.gr/" },
            { "@type": "ListItem", position: 2, name, item: url },
          ],
        }),
      });
    }
    // Ανενεργό προϊόν με όνομα: το URL μένει κανονικά indexable, αφού δείχνει
    // χρήσιμο περιεχόμενο (στοιχεία προϊόντος + προτάσεις).
    const unavailableIndexable = !!unavailable && unavailable.status === "inactive" && hasValidName;
    const title = hasValidName
      ? unavailable
        ? unavailable.status === "inactive"
          ? `${name} — προσωρινά μη διαθέσιμο | MrPanda.gr`
          : `${name} — καταργημένο προϊόν | MrPanda.gr`
        : `${name} - MrPanda.gr`
      : "MrPanda.gr";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        // Προϊόντα χωρίς έγκυρη τιμή δεν πρέπει να παρουσιάζονται στο
        // Google/Merchant (λείπει το required [price]). Τα κρύβουμε από
        // τους crawlers ώσπου να καταχωρηθεί τιμή.
        ...(unavailableIndexable
          ? []
          : recalled || restricted || discontinued || !hasValidName || price == null || !Number.isFinite(price) || price <= 0
            ? [{ name: "robots", content: "noindex, follow" }]
            : []),
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        ...(price != null && Number.isFinite(price) && price > 0 && !unavailable
          ? [
              { property: "product:price:amount", content: price.toFixed(2) },
              { property: "product:price:currency", content: "EUR" },
              { property: "product:availability", content: stock > 0 ? "in stock" : "out of stock" },
              { property: "og:price:amount", content: price.toFixed(2) },
              { property: "og:price:currency", content: "EUR" },
            ]
          : []),
      ],

      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  loader: async ({ params, context }) => {
    const prefix = idFromSlug(params.slug);
    // Οι κατηγορίες ζητούνται με useSuspenseQuery στο component: αν πέσει ένα
    // upstream subrequest στο SSR, χωρίς retry γίνεται ολόκληρη σελίδα λάθους.
    const categoriesPromise = retryAsync(() =>
      context.queryClient.ensureQueryData(categoriesQO),
    ).catch(() => null);
    const productData = await retryAsync(() =>
      context.queryClient.ensureQueryData(qo(prefix)),
    );
    await categoriesPromise;
    const productId = (productData as { product?: { id?: string } } | undefined)?.product?.id;
    // Ανενεργό/μη διαθέσιμο προϊόν: αντί για 404, 301 redirect στην κατηγορία
    // του (διατήρηση SEO από τα παλιά PrestaShop links). Αν δεν υπάρχει
    // έγκυρη κατηγορία, γυρνάμε καθαρό 404 από τον loader ώστε να μην κρασάρει
    // το SSR stream με notFound() μέσα στο component.
    if (!productId) {
      const info = await retryAsync(() =>
        getUnavailableProductInfo({ data: { idPrefix: prefix } }),
      );
      return { product: null, unavailable: info, reviewSummary: null };
    }
    let reviewSummary: { count: number; average: number } | null = null;
    if (productId) {
      try {
        const rev = await context.queryClient.ensureQueryData(reviewsQueryOptions(productId));
        reviewSummary = { count: rev.summary.count, average: rev.summary.average };
      } catch {
        reviewSummary = null;
      }
    }
    return { ...(productData as object), reviewSummary };
  },
  component: ProductPage,
});

type UnavailableInfo = Awaited<ReturnType<typeof getUnavailableProductInfo>>;

function ProductPage() {
  const loaderData = Route.useLoaderData() as { unavailable?: UnavailableInfo } | undefined;
  const info = loaderData?.unavailable;
  if (info) {
    return (
      <StorefrontLayout collapsedCart mainClassName="xmas-product">
        <UnavailableProduct
          status={info.status}
          product={info.product}
          categoryPath={info.categoryPath}
          sameCategory={info.sameCategory as never}
          sameManufacturer={info.sameManufacturer as never}
          popular={info.popular as never}
        />
      </StorefrontLayout>
    );
  }
  return <ProductPageInner />;
}

function ShareButtons({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  // Ο υπολογισμός μετά το mount αποτρέπει hydration mismatch (SSR δεν έχει URL).
  const [url, setUrl] = useState("");
  const [canNativeShare, setCanNativeShare] = useState(false);
  useEffect(() => {
    setUrl(window.location.href);
    setCanNativeShare(typeof navigator !== "undefined" && "share" in navigator);
  }, []);
  const text = `${name} — MrPanda.gr`;
  const enc = encodeURIComponent;

  const links = [
    {
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      className: "bg-[#1877F2] text-white",
    },
    {
      label: "X",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
      className: "bg-foreground text-background",
    },
    {
      label: "WhatsApp",
      Icon: MessageCircle,
      href: `https://wa.me/?text=${enc(`${text} ${url}`)}`,
      className: "bg-[#25D366] text-white",
    },
    {
      label: "Viber",
      Icon: Send,
      href: `viber://forward?text=${enc(`${text} ${url}`)}`,
      className: "bg-[#7360F2] text-white",
    },
  ];

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: name, text, url });
      } catch {
        /* user cancelled */
      }
    }
  };

  const openShare = (href: string) => {
    // Open every share target in a real top-level tab. Using a programmatic
    // anchor click (instead of window.open with a popup feature string)
    // reliably escapes a sandboxed iframe such as the Lovable preview, so
    // social sites are never loaded embedded (which they block via
    // X-Frame-Options → ERR_BLOCKED_BY_RESPONSE). App schemes (viber://) are
    // handed off to the OS the same way.
    const a = document.createElement("a");
    a.href = href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Share2 className="h-4 w-4" /> Κοινή χρήση
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {links.map(({ label, Icon, href, className }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              openShare(href);
            }}
            aria-label={`Κοινοποίηση στο ${label}`}
            title={label}
            className={`grid h-10 w-10 place-items-center rounded-full shadow-sm transition hover:scale-110 ${className}`}
          >
            <Icon className="h-5 w-5" />
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          aria-label="Αντιγραφή συνδέσμου"
          title={copied ? "Αντιγράφηκε!" : "Αντιγραφή συνδέσμου"}
          className="grid h-10 w-10 place-items-center rounded-full border border-border text-muted-foreground transition hover:scale-110 hover:text-primary"
        >
          <LinkIcon className="h-5 w-5" />
        </button>
        {canNativeShare && (
          <button
            type="button"
            onClick={nativeShare}
            aria-label="Περισσότερα"
            title="Περισσότερα"
            className="grid h-10 w-10 place-items-center rounded-full border border-border text-muted-foreground transition hover:scale-110 hover:text-primary"
          >
            <Share2 className="h-5 w-5" />
          </button>
        )}
      </div>
      {copied && <p className="mt-1 text-xs text-success">Ο σύνδεσμος αντιγράφηκε!</p>}
    </div>
  );
}

function BrandBadge({ psId, name }: { psId: number; name: string }) {
  const { data } = useQuery({
    queryKey: ["manufacturer", psId],
    queryFn: () => getManufacturerByPsId({ data: { psId } }),
    staleTime: 5 * 60_000,
  });
  const logo = data?.manufacturer?.logo_url;
  return (
    <Link
      to="/brand/$id"
      params={{ id: `${psId}-${greeklish(name)}` }}
      title={`Δείτε όλα τα προϊόντα ${name}`}
      className="flex-shrink-0 transition hover:opacity-80"
    >
      {logo ? (
        <BrandLogo logoUrl={logo} name={name} width={112} sizes="56px" className="h-14 w-14 p-1.5 text-xl shadow-sm" />
      ) : (
        <span className="inline-flex items-center rounded-lg border border-border bg-accent/40 px-3 py-2 text-sm font-semibold text-foreground shadow-sm">
          {name}
        </span>
      )}
    </Link>
  );
}

function ProductPageInner() {
  const { slug } = Route.useParams();
  const prefix = idFromSlug(slug);
  const { data } = useSuspenseQuery(qo(prefix));
  const { data: catsData } = useSuspenseQuery(categoriesQO);
  if (catsData.categories.length > 0) buildCategoryIconMap(catsData.categories);
  const cart = useCart();
  const wishlist = useWishlist();
  const { record } = useRecentlyViewed();
  const fragile = useFragileProducts();
  const imgRef = useRef<HTMLDivElement | null>(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [discOpen, setDiscOpen] = useState(false);
  // Ο loader πετάει notFound() πριν φτάσει εδώ, αλλά κρατάμε ασφαλές fallback
  // γιατί το TypeScript δεν γνωρίζει ότι το product είναι πάντα ορισμένο.
  if (!data.product) return <PlayfulNotFound />;
  const p = data.product as typeof data.product & { original_price: number | null };
  const discontinued = isDiscontinued(p.ps_id, p.name);
  useEffect(() => {
    if (discontinued) setDiscOpen(true);
  }, [discontinued, p.id]);
  useEffect(() => {
    if (p.id) record(p.id);
    // User Timing: mark when a product page is ready (key shopping moment).
    if (p.id) mark("product:viewed");
  }, [p.id, record]);
  // Gallery: φωτογραφίες προϊόντος από τον πίνακα product_images. Αν δεν
  // υπάρχουν, fallback στο κύριο image_url.
  const { data: imagesData } = useQuery({
    queryKey: ["product-images", p.id],
    queryFn: () => getProductImages({ data: { productId: p.id } }),
    staleTime: 5 * 60_000,
    enabled: !!p.id,
  });
  const gallery = useMemo(() => {
    // Η κύρια φωτογραφία (αυτή που εμφανίζεται και στις λίστες) μπαίνει πάντα
    // πρώτη. Οι υπόλοιπες από τον πίνακα product_images ακολουθούν ως επιπλέον
    // φωτογραφίες της gallery (μόνο στη σελίδα προϊόντος).
    const extra = (imagesData?.images ?? []).map((i) => i.url).filter(Boolean);
    const list: string[] = [];
    if (p.image_url) list.push(p.image_url);
    for (const url of extra) if (!list.includes(url)) list.push(url);
    return list;
  }, [imagesData, p.image_url]);
  const [activeImg, setActiveImg] = useState(0);
  useEffect(() => {
    setActiveImg(0);
  }, [p.id, gallery.length]);
  const mainImage = gallery[activeImg] ?? p.image_url;
  const inWish = wishlist.has(p.id);
  const hasSale = p.original_price != null && p.original_price > p.price;
  const displayPrice = p.price;
  const originalPrice = p.original_price;
  const rawAvailability = resolveAvailability(
    (p as { availability?: string | null }).availability,
    (p as { stock_quantity?: number | null }).stock_quantity,
  );
  const availability = (() => {
    const v = rawAvailability.toLowerCase();
    if (v.includes("άμεσα")) return "Άμεσα Διαθέσιμο, Υπάρχει διαθέσιμο στο φυσικό μας κατάστημα";
    if (v.includes("διαθέσιμο")) return "Διαθέσιμο, Παραλαβή από το κατάστημα κατόπιν παραγγελίας την επόμενη εργάσιμη ημέρα";
    if (v.includes("παραγγελ")) return "Κατόπιν παραγγελίας σε 4-10 εργάσιμες ημέρες";
    return rawAvailability;
  })();
  const shortRaw = ((p as { description_short?: string | null; description?: string | null }).description_short || (p as { description?: string | null }).description || "").trim();
  const fullRaw = ((p as { description?: string | null }).description || "").trim();
  const shortDescriptionHtml = sanitizeHtml(shortRaw);
  const fullDescriptionHtml = sanitizeHtml(fullRaw);
  const shortIsHtml = hasHtmlTags(shortRaw);
  const fullIsHtml = hasHtmlTags(fullRaw);
  const availabilityStyle = availabilityColor(rawAvailability);
  const depositWithVatUnit = (() => {
    const d = Number((p as { bottle_deposit?: number | null }).bottle_deposit ?? 0);
    if (!(d > 0)) return null;
    const vat = Number((p as { tax_rate?: number | null }).tax_rate ?? 24);
    return d * (1 + vat / 100);
  })();
  const payload = { productId: p.id, psId: p.ps_id, name: p.name, price: Number(displayPrice), imageUrl: p.image_url, stock: p.stock_quantity, deposit: depositWithVatUnit, taxRate: Number(p.tax_rate ?? 24) };
  const isFragile = fragile.isFragile(
    p.ps_id,
    (p as { reference?: string | null }).reference ?? null,
  );

  // Build breadcrumb chain from root → leaf using categories tree
  const crumbs: Crumb[] = (() => {
    const all = catsData.categories ?? [];
    const byPsId = new Map<number, { ps_id: number; name: string; parent_ps_id: number | null }>();
    for (const c of all) byPsId.set(c.ps_id, c);
    const chain: Crumb[] = [];
    let cur = p.category_id != null ? byPsId.get(p.category_id) : undefined;
    const seen = new Set<number>();
    while (cur && !seen.has(cur.ps_id)) {
      seen.add(cur.ps_id);
      chain.unshift({ label: cur.name, psId: cur.ps_id });
      cur = cur.parent_ps_id != null ? byPsId.get(cur.parent_ps_id) : undefined;
    }
    // Drop the synthetic PrestaShop "Home" root if present (ps_id=2 / depth 1)
    if (chain.length > 1 && /^(home|αρχική)$/i.test(chain[0].label.trim())) chain.shift();
    return chain;
  })();

  const addWithFly = () => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (rect) flyToCart({ fromRect: rect, imageUrl: p.image_url, onArrive: () => cart.add(payload, qty) });
    else cart.add(payload, qty);
    setAddedToCart(true);
  };
  return (
    <StorefrontLayout mainClassName="xmas-product">
      <Breadcrumbs items={crumbs} />
      <div className="mx-auto max-w-none px-4 pb-12">
        <div className="grid gap-8 md:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-3 md:col-span-1">
        <div
          ref={imgRef}
          draggable={p.stock_quantity > 0 && !discontinued}
          onDragStart={(e) => {
            if (p.stock_quantity <= 0 || discontinued) return;
            e.dataTransfer.effectAllowed = "copy";
            const imgEl = (imgRef.current?.querySelector("img[data-product-image]") ?? null) as HTMLImageElement | null;
            setPlayfulProductDragImage(e.dataTransfer, payload, imgEl);
            e.dataTransfer.setData(CART_DRAG_MIME, serializeDrag(payload));
            e.dataTransfer.setData("text/plain", p.name);
            showCartDropHint();
          }}
          onDragEnd={() => hideCartDropHint()}
          className="xmas-surface-grad group relative cursor-grab overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-accent/40 via-card to-background p-6 shadow-sm transition-shadow animate-pop-in hover:shadow-md active:cursor-grabbing"
        >
          {hasSale && (
            <span className="pointer-events-none absolute left-0 top-12 z-10 animate-bob rounded-r-full bg-destructive px-3 py-1 text-xs font-bold text-destructive-foreground shadow">
              🔥 Προσφορά
            </span>
          )}
          {mainImage && (
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              aria-label="Μεγέθυνση εικόνας"
              title="Μεγέθυνση"
              className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-background/90 text-muted-foreground shadow transition hover:scale-110 hover:text-primary"
            >
              <Search className="h-5 w-5" />
            </button>
          )}
          <ProductImage
            src={mainImage}
            alt={productImageAlt(p)}
            width={700}
            loading="eager"
            draggable={false}
            className="mx-auto aspect-square w-[90%] max-w-none object-contain transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-1 md:w-full"
            fallback={<div className="mx-auto grid aspect-square w-[90%] max-w-none place-items-center text-9xl animate-bob md:w-full">🐼</div>}
          />
        </div>
        {gallery.length > 1 && (
          <div className="grid grid-cols-5 gap-2">
            {gallery.map((url, i) => (
              <button
                key={`${url}-${i}`}
                type="button"
                onClick={() => setActiveImg(i)}
                aria-label={`Φωτογραφία ${i + 1}`}
                aria-current={i === activeImg}
                className={`overflow-hidden rounded-xl border bg-card p-1 transition hover:border-primary ${
                  i === activeImg ? "border-primary ring-2 ring-primary/30" : "border-border"
                }`}
              >
                <ProductImage
                  src={url}
                  alt={`${productImageAlt(p)} — φωτογραφία ${i + 1}`}
                  width={120}
                  draggable={false}
                  className="aspect-square w-full object-contain"
                  fallback={<div className="grid aspect-square w-full place-items-center text-2xl">🐼</div>}
                />
              </button>
            ))}
          </div>
        )}
        </div>
        <div className="relative z-10 flex min-w-0 flex-col md:col-span-2">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <h1 className="min-w-0 break-words text-2xl font-extrabold tracking-tight text-foreground">{p.name}</h1>
            {p.manufacturer_id != null && p.manufacturer_name && (
              <BrandBadge psId={p.manufacturer_id} name={p.manufacturer_name} />
            )}
          </div>
          <HeaderRating productId={p.id} />
          {p.ps_id != null && <div className="mt-1 text-xs text-muted-foreground">Κωδικός Eshop: {p.ps_id}</div>}
          {shortDescriptionHtml && (
            shortIsHtml ? (
              <div className={`mt-3 ${proseClass}`} dangerouslySetInnerHTML={{ __html: shortDescriptionHtml }} />
            ) : (
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{shortDescriptionHtml}</p>
            )
          )}
          {p.ean13 && (
            <div className="mt-2">
              <ExpiryBadge ean={p.ean13} />
            </div>
          )}
          {(p as { health_score?: number | null }).health_score != null && (
            <HealthScoreBanner
              score={Number((p as { health_score?: number | null }).health_score)}
              className="mt-3"
            />
          )}
          {(p as { health_score?: number | null }).health_score == null && (
            <IngredientScoreBanner productId={p.id} className="mt-3" />
          )}
          <div className="xmas-surface-grad my-4 rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-accent/30 to-background p-5 shadow-sm">
            {discontinued ? (
              <div className="flex items-center gap-3">
                <div className="text-3xl animate-bob">🐼</div>
                <div>
                  <div className="text-lg font-extrabold text-foreground">Δεν διατίθεται πλέον</div>
                  <div className="text-sm text-muted-foreground">Το προϊόν αυτό αποσύρθηκε από τη γκάμα μας.</div>
                </div>
              </div>
            ) : (
              <>
            {hasSale && (
              <div className="text-sm text-muted-foreground line-through">
                {formatPrice(originalPrice ?? p.price)}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className={`text-4xl font-extrabold tracking-tight ${hasSale ? "text-destructive" : "text-price"}`}>
                {formatPrice(displayPrice)}
                <span className="ml-1.5 align-middle text-xs font-medium text-muted-foreground">με ΦΠΑ</span>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-bold text-foreground">
                <span aria-hidden className="animate-bob text-base">🚐</span>
                Θεσσαλονίκη: δωρεάν μεταφορικά από <span className="text-price">25,00 €</span>
              </span>
            </div>
            {(() => {
              const unit = computeUnitPrice(p.name, displayPrice);
              if (!unit) return null;
              return (
                <div className="mt-1.5 text-sm font-semibold text-muted-foreground">
                  {formatPrice(unit.value)} / {unit.unitLabel}
                  <span className="ml-1 font-normal">({unit.quantityLabel})</span>
                </div>
              );
            })()}
            {(() => {
              const deposit = Number((p as { bottle_deposit?: number | null }).bottle_deposit ?? 0);
              if (deposit <= 0) return null;
              const vatRate = Number((p as { tax_rate?: number | null }).tax_rate ?? 24);
              const depositWithVat = deposit * (1 + vatRate / 100);
              return (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">🍾</span>
                  Συμπεριλαμβάνεται εγγυοδοσία φιάλης {formatPrice(depositWithVat)}
                </div>
              );
            })()}
            {p.stock_quantity <= 0 ? (
              <div className="mt-1 text-sm text-destructive">Εξαντλημένο</div>
            ) : p.stock_quantity < 20 ? (
              <div className="mt-1 text-sm font-medium text-destructive">Έμειναν μόνο {p.stock_quantity} τεμάχια</div>
            ) : null}
            {hasSale && (
              <OfferCountdown
                endsAt={(p as { offer_ends_at?: string | null }).offer_ends_at}
                className="mt-3"
              />
            )}
              </>
            )}
          </div>
          {availability && !discontinued && (
            <p className={`mb-3 flex items-center gap-2 text-sm font-medium italic ${availabilityStyle}`}>
              <span className="inline-block h-2 w-2 rounded-full bg-current" />
              {availability}
            </p>
          )}
          {p.stock_quantity > 0 && !discontinued && (
            <KlarnaMessaging amount={displayPrice} className="mb-4" />
          )}
          {isFragile && !discontinued && (
            <div className="mb-4 rounded-2xl border-2 border-destructive/40 bg-destructive/5 p-4">
              <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-destructive/10 text-destructive">
                  <PackageX className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-destructive">
                    Εύθραυστο προϊόν — δεν αποστέλλεται με courier
                  </p>
                  <p className="mt-1 text-sm text-foreground">
                    Λόγω της ευθραυστότητάς του, το προϊόν αυτό διατίθεται μόνο με{" "}
                    <strong>παραλαβή από το κατάστημα</strong> ή{" "}
                    <strong>παράδοση MrPanda εντός Θεσσαλονίκης με δικά μας οχήματα</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}
          {!discontinued && (
          <div className="flex gap-2">
            <div className="flex items-center rounded-full border border-border bg-background">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={p.stock_quantity <= 0 || qty <= 1}
                className="grid h-12 w-11 place-items-center rounded-full text-lg font-semibold text-foreground transition hover:bg-primary/10 hover:text-primary active:scale-90 disabled:opacity-40"
                aria-label="Μείωση τεμαχίων"
              >
                −
              </button>
              <input
                type="text"
                inputMode="numeric"
                value={qty}
                onChange={(e) => {
                  const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
                  if (Number.isNaN(n)) {
                    setQty(1);
                    return;
                  }
                  const max = p.stock_quantity > 0 ? p.stock_quantity : n;
                  setQty(Math.min(Math.max(1, n), max));
                }}
                onBlur={(e) => {
                  if (!e.target.value.trim()) setQty(1);
                }}
                className="w-10 bg-transparent text-center font-semibold tabular-nums outline-none"
                aria-label="Τεμάχια"
              />
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(p.stock_quantity > 0 ? p.stock_quantity : q + 1, q + 1))}
                disabled={p.stock_quantity <= 0 || qty >= p.stock_quantity}
                className="grid h-12 w-11 place-items-center rounded-full text-lg font-semibold text-foreground transition hover:bg-primary/10 hover:text-primary active:scale-90 disabled:opacity-40"
                aria-label="Αύξηση τεμαχίων"
              >
                +
              </button>
            </div>
            <button
              onClick={addWithFly}
              disabled={p.stock_quantity <= 0}
              className="mp-bouncy flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 font-semibold text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-50"
            >
              <ShoppingCart className="h-5 w-5" /> Προσθήκη στο καλάθι
            </button>
            <button
              onClick={() => wishlist.toggle({ productId: p.id, psId: p.ps_id, name: p.name, price: Number(p.price), imageUrl: p.image_url })}
              className="mp-bouncy grid h-12 w-12 place-items-center rounded-full border border-border hover:border-primary hover:text-primary"
              aria-label="Wishlist"
            >
              <Heart className={`h-5 w-5 ${inWish ? "fill-primary text-primary" : ""}`} />
            </button>
          </div>
          )}
          {p.stock_quantity > 0 && !discontinued && (
            <button
              type="button"
              onClick={() => setPickupOpen(true)}
              className="mp-bouncy xmas-pickup mt-3 flex w-full flex-col items-center justify-center gap-1 rounded-full border border-primary/40 px-4 py-3 font-semibold text-primary shadow-sm transition"
            >
              <span className="flex items-center gap-2">
                <Store className="h-5 w-5" /> Παρέλαβε από το κατάστημα
              </span>
              <span className="text-xs font-medium opacity-80">Παράγγειλε τώρα και παρέλαβέ τα {pickupTimeEstimate()}</span>
            </button>
          )}
          <ShareButtons name={p.name} />
        </div>
        </div>
        <ChristmasStrip className="px-0 py-4" />
        {addedToCart && p.category_id != null && !discontinued && (
          <CrossSell
            categoryIds={[p.category_id]}
            excludeProductIds={[p.id, ...cart.items.map((it) => it.productId)]}
            className="mt-8"
            title="Αγόρασέ το μαζί με… 🎯"
          />
        )}
        {hasVisibleContent(fullDescriptionHtml) && (
          <div className="xmas-surface mt-8 rounded-3xl border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground">✨ Περιγραφή</h2>
            {fullIsHtml ? (
              <div className={proseClass} dangerouslySetInnerHTML={{ __html: fullDescriptionHtml }} />
            ) : (
              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{fullDescriptionHtml}</p>
            )}
          </div>
        )}
        <NutritionFacts productId={p.id} healthScore={(p as { health_score?: number | null }).health_score ?? null} />
        <IngredientAnalysis productId={p.id} />
        <Suspense fallback={<div className="mt-10 h-24" />}>
          <ProductReviews productId={p.id} productPsId={p.ps_id ?? undefined} />
        </Suspense>
        <RecentlyViewed excludeId={p.id} />
        <RelatedProducts
          excludeId={p.id}
          categoryId={p.category_id ?? undefined}
          manufacturerId={p.manufacturer_id ?? undefined}
          manufacturerName={p.manufacturer_name ?? undefined}
        />
      </div>
      {zoomOpen && mainImage && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setZoomOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        >
          <button
            type="button"
            onClick={() => setZoomOpen(false)}
            aria-label="Κλείσιμο"
            className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-background/90 text-foreground shadow hover:text-primary"
          >
            <X className="h-5 w-5" />
          </button>
          <ImageZoomLens
            src={mainImage}
            alt={productImageAlt(p)}
            className="h-[85vh] w-[85vw]"
            zoomLevel={2.5}
            lensSize={200}
          />
        </div>
      )}
      <StorePickupDialog
        open={pickupOpen}
        onOpenChange={setPickupOpen}
        initialQty={qty}
        product={{
          id: p.id,
          name: p.name,
          price: Number(displayPrice),
          stock: p.stock_quantity,
          imageUrl: p.image_url,
        }}
      />
      <DiscontinuedPopup open={discOpen} onOpenChange={setDiscOpen} />
      {/* Child routes (π.χ. /p/$slug/review) εμφανίζονται πάνω από τη σελίδα */}
      <Outlet />
    </StorefrontLayout>
  );
}

function RelatedRow({
  title,
  emoji,
  Icon,
  products,
}: {
  title: string;
  emoji: string;
  Icon: typeof Sparkles;
  products: unknown[];
}) {
  if (products.length === 0) return null;
  // 2 σειρές με 6 προϊόντα ανά σειρά στο desktop (lg:grid-cols-6) → 12 προϊόντα.
  const items = products.slice(0, 12);
  return (
    <section className="mt-10">
      <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground">
        <span className="animate-bob" aria-hidden>{emoji}</span>
        <Icon className="hidden h-5 w-5 text-primary sm:block" />
        {title}
      </h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {items.map((p) => (
          <ProductCard key={(p as { id: string }).id} product={p as never} />
        ))}
      </div>
    </section>
  );
}

function RelatedProducts({
  excludeId,
  categoryId,
  manufacturerId,
  manufacturerName,
}: {
  excludeId: string;
  categoryId?: number;
  manufacturerId?: number;
  manufacturerName?: string;
}) {
  const { data } = useQuery({
    queryKey: ["product", "related", excludeId, categoryId ?? 0, manufacturerId ?? 0],
    queryFn: () =>
      listRelatedProducts({ data: { excludeId, categoryId, manufacturerId, limit: 12 } }),
    staleTime: 5 * 60_000,
    enabled: categoryId != null || manufacturerId != null,
  });
  const similar = data?.similar ?? [];
  const sameManufacturer = data?.sameManufacturer ?? [];
  return (
    <>
      <RelatedRow
        title="Παρόμοια προϊόντα"
        emoji="✨"
        Icon={Sparkles}
        products={similar}
      />
      <RelatedRow
        title={manufacturerName ? `Περισσότερα από ${manufacturerName}` : "Από τον ίδιο κατασκευαστή"}
        emoji="🏭"
        Icon={Factory}
        products={sameManufacturer}
      />
    </>
  );
}

// Compact rating summary under the product title, links to the reviews section.
function HeaderRating({ productId }: { productId: string }) {
  const { data } = useQuery(reviewsQueryOptions(productId));
  const summary = data?.summary;
  if (!summary || summary.count === 0) return null;
  return (
    <a
      href="#reviews"
      className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <RatingStars value={summary.average} size={15} />
      <span className="font-medium">{summary.average.toFixed(1)}</span>
      <span>
        ({summary.count} {summary.count === 1 ? "αξιολόγηση" : "αξιολογήσεις"})
      </span>
    </a>
  );
}

// "Πρόσφατα είδες" carousel: products the user viewed recently (localStorage),
// excluding the current product.
function RecentlyViewed({ excludeId }: { excludeId: string }) {
  const { ids } = useRecentlyViewed();
  const wanted = ids.filter((id) => id !== excludeId).slice(0, 12);
  const { data } = useQuery({
    queryKey: ["product", "recently-viewed", wanted.join(",")],
    queryFn: () => listProductsByIds({ data: { ids: wanted } }),
    enabled: wanted.length > 0,
    staleTime: 60_000,
  });
  const products = data?.products ?? [];
  if (wanted.length === 0 || products.length === 0) return null;
  return <RelatedRow title="Πρόσφατα είδες" emoji="🕒" Icon={Clock} products={products} />;
}
